import { getVisitsPerYear } from './estimateEngine';

/**
 * Apply per-estimate, per-service rate overrides on top of the engine output.
 *
 * The estimate engine (buildServiceLinesFromTakeoff) fuses frequency + coverage
 * into each task's hoursPerVisit / annualHours at build time. The on-screen
 * frequency / coverage chips edit those values for THIS estimate only by writing
 * `rateOverrides[serviceCode] = { frequency?, coverage? }`. This pure helper
 * re-derives the affected task hours so the screen (and saved draft) reflect the
 * chip choices without touching the engine.
 *
 * Coverage scales effort linearly: dropping coverage 100% -> 50% halves
 * hoursPerVisit (matches how the irrigation line already treats its 0.5 factor).
 *
 * @param {Array} services       service lines from buildServiceLinesFromTakeoff
 * @param {Object} rateOverrides { [serviceCode]: { frequency?, coverage? } }
 * @param {Object} settings      estimate settings (for frequency presets)
 * @returns {Array} new services array; only overridden tasks are recomputed
 */
export function applyRateOverrides(services = [], rateOverrides = {}, settings) {
  if (!rateOverrides || !Object.keys(rateOverrides).length) return services;

  const applyToTask = (task) => {
    const override = rateOverrides[task.serviceCode];
    if (!override) return task;

    const next = { ...task };

    if (override.coverage != null) {
      const baseCoverage = Number(task.coverage) > 0 ? Number(task.coverage) : 1;
      const scale = Number(override.coverage) / baseCoverage;
      next.coverage = Number(override.coverage);
      next.hoursPerVisit = (Number(task.hoursPerVisit) || 0) * scale;
    }

    if (override.frequency != null) {
      next.frequency = override.frequency;
    }

    next.annualHours = (Number(next.hoursPerVisit) || 0) * getVisitsPerYear(next.frequency, settings);
    return next;
  };

  const applyToTaskList = (tasks) => {
    if (!Array.isArray(tasks) || !tasks.length) return { tasks, touched: false };
    let touched = false;
    const nextTasks = tasks.map((task) => {
      if (!rateOverrides[task.serviceCode]) return task;
      touched = true;
      return applyToTask(task);
    });
    return { tasks: nextTasks, touched };
  };

  return services.map((service) => {
    if (!Array.isArray(service.tasks) || !service.tasks.length) return service;

    const main = applyToTaskList(service.tasks);
    const routine = applyToTaskList(service.routineTasks);
    const periodic = applyToTaskList(service.periodicTasks);
    const touched = main.touched || routine.touched || periodic.touched;

    return touched
      ? {
          ...service,
          tasks: main.tasks,
          routineTasks: routine.tasks,
          periodicTasks: periodic.tasks,
        }
      : service;
  });
}
